"use client"

import React, {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import classNames from "classnames/bind"
import { format } from "date-fns"

import { Popover, PopoverContent, PopoverTrigger } from "@fsg/gui-bits"

import moduleStyles from "./DatePicker.module.scss"

import "react-day-picker/dist/style.css"


import { CalendarClock } from "@fsg/design-system/svg/icons"
import { cn } from "@fsg/utils"

import { usePlaceholder } from "@app/utils/usePlaceholder"

import { Calendar } from "./calendar"
import { ChevronDown } from "lucide-react"
import { onBlurWorkaround } from "@app/utils"

const cx = classNames.bind(moduleStyles)

type DatePickerProps = {
  selected?: Date
  onSelect: (input: Date) => void
  dateFormat?: string
  showCalendarIcon?: boolean
  showCaret?: boolean
  styles?: {
    content?: string
  }
  buttonClassName?: string
  placeholder?: string
  isEditing?: boolean
  label?: string
  renderSelected?: (value: {
    selected: Date
    formatted: string
  }) => React.ReactNode
}

export const DatePicker = forwardRef(
  (props: DatePickerProps, ref: Ref<HTMLButtonElement>) => {
    const {
      selected,
      onSelect,
      dateFormat = "PPP",
      showCalendarIcon = false,
      showCaret = false,
      buttonClassName,
      styles,
      placeholder,
      label,
    } = props
    const [isOpen, setIsOpen] = useState(false)
    const defaultPlaceholder = usePlaceholder(placeholder)

    const datePickerRef = useRef<HTMLDivElement | null>(null)

    function handleOpen() {
      setIsOpen(true)
    }
    const handleClose = useCallback(() => setIsOpen(false), [])

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          datePickerRef.current &&
          event.target instanceof Node &&
          !datePickerRef.current.contains(event.target)
        ) {
          handleClose()
        }
      }

      document.addEventListener("mousedown", handleClickOutside)
      return () => {
        document.removeEventListener("mousedown", handleClickOutside)
      }
    }, [handleClose])

    function handleSelect(input: Date) {
      onSelect(input)
      handleClose()
    }

    const selectedDate = {
      selected,
      formatted:
        selected !== undefined && selected != null
          ? format(selected, dateFormat)
          : (defaultPlaceholder as string),
    }

    const renderedDate =
      props.renderSelected !== undefined ? (
        props.renderSelected(selectedDate)
      ) : (
        <span className="text-xs">
          {selected !== undefined && selected != null
            ? selectedDate.formatted
            : defaultPlaceholder}
        </span>
      )

    return (
      <Popover open={isOpen} onOpenChange={handleOpen}>
        <PopoverTrigger asChild>
          <div
            className={cn(
              "flex cursor-pointer items-center gap-3xs whitespace-nowrap rounded-xs border border-gray p-xs",
              buttonClassName
            )}
            onBlur={onBlurWorkaround}
            tabIndex={0}
          >
            {/* ICON */}
            {showCalendarIcon ? (
              <CalendarClock className="w-[16px] h-[16px]" />
            ) : null}
            {/* LABEL */}
            {label ? <span className="text-sm">{label}</span> : null}
            {/* DATE */}
            {renderedDate}
            {/* CARET */}
            {showCaret && (
              <ChevronDown
                className="text-gray-900"
                width="14px"
                height="14px"
              />
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent className={cn("pointer-events-auto w-auto bg-white p-0", styles?.content)}       
       >
          <div style={{width: 330, height: 335, position: 'relative'}} ref={datePickerRef}>
            <Calendar
              modifiersClassNames={{
                selected: cx("selected-bg"),
              }}
              mode="single"
              selected={selected}
              onSelect={handleSelect}
              fixedWeeks
            />
          </div>
        </PopoverContent>
      </Popover>
    )
  }
)
DatePicker.displayName = "DatePicker"
